import { Card, Col, message, Row, Spin } from "antd";
import React, { useEffect, useState } from "react";
import moment from "moment";

import PredeterminationPatientsTable from "./PredeterminationPatientsTable";
import PredeterminationUploadedFiles from "./PredeterminationUploadedFiles";

import { controller as treatmentsController } from "../../../TreatmentPlans/controller";
import { statusLableColor } from "../statusLableColor";
import { controller } from "../../../../controller";

import calendarCircle from "../../../../assets/icons/calendar-circle.svg";
import directBox from "../../../../assets/icons/directbox-receive.svg";
import users from "../../../../assets/icons/profile-2user.svg";

export default function PredeterminationDetails({
  setSwitchToPredeterminationDetails,
  setPredeterminationTreatmentID,
  predeterminationOrPostPayment,
  setShowPredeterminationModal,
  predeterminationTreatmentID,
  setPredeterminationName,
  predeterminationBatchID,
  eligibilityData: predeterminationData,
  setIsApproved,
  insuranceID,
  isApproved,
  patient,
}) {
  const [isLoadingInsuranceData, setIsLoadingInsuranceData] = useState(false);
  const [data, setData] = useState(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isApprovingPostPayment, setIsApprovingPostPayment] = useState([]); // Loading for the time that all patients are fetching
  const [isGettingPatients, setIsGettingPatients] = useState(false); // Loading for the time that all patients are fetching
  const [isGettingProcedures, setIsGettingProcedures] = useState(false); // Loading for the time that all patients are fetching
  const [allPatients, setAllPatients] = useState([]); // All office patients
  const [allProcedures, setAllProcedures] = useState([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState({});
  const [approvedPatients, setApprovedPatients] = useState([]);
  const [allPaymentTypes, setAllpaymentTypes] = useState([]);
  const [insuranceStatus, setInsuranceStatus] = useState(undefined);
  const [insuranceStatusLabelColor, setInsuranceStatusLabelColor] = useState({ statusBackgroundColor: null, statusColor: null });
  const [markedProcedures, setMarkedProcedures] = useState([]);
  const [showApprovedView, setShowApprovedView] = useState(false);

  // SECTION: Get required data
  // Getting all office patients
  useEffect(function () {
    async function getAllPatients() {
      try {
        setIsGettingPatients(true);
        const res = await controller.getPatient2();
        setAllPatients(res);
      } catch (err) {
        message.error("Something went wrong while fetching patients");
        console.error(err);
      } finally {
        setIsGettingPatients(false);
      }
    }
    getAllPatients();
  }, []);

  // Getting all procedures
  useEffect(function () {
    async function getAllProcedures() {
      try {
        setIsGettingProcedures(true);
        const res = await treatmentsController.getProcedure(0, "");
        setAllProcedures(res.json);
      } catch (err) {
        message.error("Something went wrong while fetching procedures");
        console.error(err);
      } finally {
        setIsGettingProcedures(false);
      }
    }
    getAllProcedures();
  }, []);

  // Store all payment types after fetching data
  useEffect(
    function () {
      if (data !== null) {
        setAllpaymentTypes(data.payment_types);
      } else if (predeterminationData !== undefined) {
        setAllpaymentTypes(predeterminationData.payment_types);
      } else {
        return;
      }
    },
    [data, predeterminationData]
  );

  // Update insurance status
  useEffect(
    function () {
      const { statusBackgroundColor, statusColor } = statusLableColor(insuranceStatus);
      setInsuranceStatusLabelColor({ statusBackgroundColor, statusColor });
    },
    [insuranceStatus]
  );

  // Getting uploaded files data
  useEffect(
    function () {
      if (insuranceID === null) return;
      getInsuranceDetailsData();
    },
    [insuranceID]
  );

  // Set the predetermination data after the agent task is done
  useEffect(
    function () {
      if (predeterminationData !== undefined) {
        setInsuranceStatus(predeterminationData.status);
        setAllpaymentTypes(predeterminationData.payment_types);
        setPredeterminationName(predeterminationData.name);
        setData(predeterminationData);
      }
    },
    [predeterminationData]
  );

  // Get and check predetermination status each 5 seconds
  useEffect(
    function () {
      if (!data || data.status !== "processing") return;

      const intervalID = setInterval(getInsuranceDetailsData, 5000);

      return () => clearInterval(intervalID);
    },
    [data]
  );

  // SECTION: Functions
  // Getting insurance details
  const getInsuranceDetails = async () => {
    if (insuranceID) {
      const response = await controller.fetchPredeterminationDetails(insuranceID);

      const { statusBackgroundColor, statusColor } = statusLableColor(response.json.status);

      setInsuranceStatusLabelColor({ statusBackgroundColor, statusColor });
      setPredeterminationName(response.json.name);
      setData(response.json);
      setInsuranceStatus(response.json.status);
    }
  };

  // Fetch the insurance data
  async function getInsuranceDetailsData() {
    try {
      setIsLoadingInsuranceData(true);
      await getInsuranceDetails();
    } catch (err) {
      message.error("Something went wrong while getting insurance details data.");
      console.error(err);
    } finally {
      setIsLoadingInsuranceData(false);
    }
  }

  if (isLoadingInsuranceData || isGettingPatients || isGettingProcedures || !data)
    return (
      <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, gap: 10 }}>
        <Spin />
        loading...
      </div>
    );

  const allInsurancePatients = data.patients.map((patient) => ({ ...patient }));

  // This approves the insurance in the predetermination page
  const handleApproveInsurance = async () => {
    try {
      setIsApproving(true);
      const insurancePatient = data.patients[0]; // Get the patient in the insurance, of course this won't correctly if there were more than two patients

      const requiredDataToSetPatient = {
        patient_id: patient.id,
        temporary_patient_id: insurancePatient.id,
        treatment_procedure_ids: markedProcedures,
        predetermination_request_id: insuranceID || predeterminationBatchID,
        predetermination_case_id: insurancePatient.predetermination_request_submission_id,
      };

      let response;
      if (predeterminationTreatmentID) {
        requiredDataToSetPatient.treatment_id = predeterminationTreatmentID;
        response = await controller.setTreatmentPredeterminationID(requiredDataToSetPatient);
      } else {
        response = await controller.setTreatmentPatientId(requiredDataToSetPatient);
      }

      if (response.status === 200) {
        setIsApproved(true);
        const { statusBackgroundColor, statusColor } = statusLableColor("approved");
        setInsuranceStatus("approved");
        setInsuranceStatusLabelColor({ statusBackgroundColor, statusColor });
        message.success("Approved successfully!");
        // const approveResponse = await controller.approvePreditermination(insuranceID);

        setShowApprovedView(true);

        if (predeterminationTreatmentID) {
          setPredeterminationTreatmentID(undefined);
        }

        setTimeout(() => {
          setSwitchToPredeterminationDetails(false);
          setShowPredeterminationModal(false);
          setIsApproved(false);
        }, 3000);
      }
    } catch (e) {
      console.error(e);
      message.error("Something went wrong while approving insurance");
    } finally {
      setIsApproving(false);
      await getInsuranceDetails();
    }
  };

  // This approves the patient with his/her procedures
  async function handleApprovePostPayment(patientID) {
    if (!Object.keys(selectedPaymentType).includes(patientID.toString())) {
      message.error("Please select payment type for this patient");
      return;
    }

    try {
      setIsApprovingPostPayment((prevIDs) => [...prevIDs, patientID]);

      // Getting all patient procedure ids
      const procedureIDs = allInsurancePatients.flatMap((patient) =>
        patient.id === patientID ? patient.insurances.map((insurance) => insurance.id) : []
      );

      const dataToSend = { procedure_ids: procedureIDs, patient_id: patientID, payment_type: selectedPaymentType[patientID] };

      const response = await controller.approvePostPayment(dataToSend);

      if (response.status < 250) {
        message.success("Approved successfully!");

        setApprovedPatients((prevApprovedPatients) => [...prevApprovedPatients, patientID]);

        if (approvedPatients.length + 1 === allInsurancePatients.length) {
          setIsApproved(true);
          setInsuranceStatus(response.json.status);
        }
      }
    } catch (err) {
      message.error("Something went wrong while approving post payment");
      console.error(err);
    } finally {
      setIsApprovingPostPayment((prevIDs) => prevIDs.filter((id) => id !== patientID));
    }
  }

  return (
    <div style={{ padding: "20px 0", position: "relative" }}>
      {/* TODO */}
      {showApprovedView && (
        <div className="approved-view">
          <span className="approved-view-message">Approved Successfully</span>
        </div>
      )}

      <Card>
        <div>
          <Row className="post-payment-status">
            <p style={{ fontSize: 18, marginBottom: 0 }}>Patient Insurace Information</p>

            <div>
              <span style={{ color: "#848696", fontSize: 16 }}>Status: </span>
              <span
                style={{
                  backgroundColor: insuranceStatusLabelColor.statusBackgroundColor,
                  color: insuranceStatusLabelColor.statusColor,
                  padding: "1px 24px",
                  borderRadius: 100,
                  fontSize: 14,
                  textTransform: "capitalize",
                }}
              >
                {insuranceStatus === "accepted" || insuranceStatus === "approved" || isApproved
                  ? "approved"
                  : insuranceStatus || (data && data.status)}
              </span>
            </div>
          </Row>

          <Row className="submission-received-dates-files">
            {/* Approve all date */}
            <Col style={{ display: "flex", gap: 5, fontSize: 16, whiteSpace: "nowrap" }}>
              <img src={calendarCircle} alt="calendar-circle" />
              <span style={{ color: "#848696" }}>Approval Date:</span>
              <span>{data && data.approval_date && moment(data.approval_date).format("YYYY-MM-DD")}</span>
            </Col>

            {/* Upload date */}
            <Col style={{ display: "flex", gap: 5, fontSize: 16, whiteSpace: "nowrap" }}>
              <img src={directBox} alt="direct-box" />
              <span style={{ color: "#848696" }}>Uploaded Date:</span>
              <span>{data && data.upload_date && moment(data.upload_date).format("YYYY-MM-DD")}</span>
            </Col>

            {/* Number of Patients */}
            <Col style={{ display: "flex", gap: 5, fontSize: 16, whiteSpace: "nowrap" }}>
              <img src={users} alt="folder" />
              <span style={{ color: "#848696" }}>Patients: </span>
              <span>{data && data.patients.length}</span>
            </Col>
          </Row>
        </div>

        <Card>
          <p style={{ fontSize: 18 }}>Patient Insurance Result</p>

          <div style={{ position: "relative" }}>
            <PredeterminationPatientsTable
              predeterminationOrPostPayment={predeterminationOrPostPayment}
              handleApprovePostPayment={handleApprovePostPayment}
              isApprovingPostPayment={isApprovingPostPayment}
              setSelectedPaymentType={setSelectedPaymentType}
              handleApproveInsurance={handleApproveInsurance}
              selectedPaymentType={selectedPaymentType}
              setMarkedProcedures={setMarkedProcedures}
              approvedPatients={approvedPatients}
              allPaymentTypes={allPaymentTypes}
              insuranceStatus={insuranceStatus}
              patients={allInsurancePatients}
              allProcedures={allProcedures}
              allPatients={allPatients}
              isApproving={isApproving}
              selectedInsurance={data}
              isApproved={isApproved}
              status={data.status}
              patient={patient}
            />
          </div>
        </Card>

        {data && data.pdf_files && data.pdf_files.length > 0 && (
          <div style={{ marginTop: 30 }}>
            <p style={{ fontSize: 18 }}>Uploaded Files</p>

            <div style={{ display: "flex", alignItems: "center", flexDirection: "row", flexWrap: "wrap", gap: 20 }}>
              {data.pdf_files.map((file, index) => (
                <PredeterminationUploadedFiles key={index} file={file} />
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Approved Button For Predetermination */}
      {/* {predeterminationOrPostPayment === "predetermination" && (
        <div style={{ marginTop: 30, display: "flex", marginBottom: 90 }}>
          <Button
            type="primary"
            onClick={handleApproveInsurance}
            disabled={data.status !== "pending" || isApproved || isApproving}
            style={{
              backgroundColor: data.status === "approved" || isApproved ? "rgba(4, 201, 0, 0.1)" : data.status !== "pending" ? "#F5F5F5" : "#6B43B5",
              color: data.status === "approved" || isApproved ? "#04C900" : data.status !== "pending" ? "#848696" : "white",
              border: data.status === "approved" || (isApproved && "none"),
              padding: "20px 50px",
              marginLeft: "auto",
            }}
            className={`${isApproved ? "approved-button" : ""}`}
          >
            {isApproving ? "Loading..." : !(data.status === "processing" || data.status === "pending") || isApproved ? "Approved" : "Approve All"}
          </Button>
        </div>
      )} */}
    </div>
  );
}
